import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { DataState } from "../../../shared/models";
import type { SkyStamp } from "../../../shared/sky/types";
import { addDays, todayIn } from "../../../shared/dates";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import type { IconName } from "../../ui/Icon";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { formatTime, relativeAge } from "../../ui/time";
import "./sky.css";

/** Empty state shown when the household has no location yet (mirrors the weather widget). */
export function NeedsLocation({ icon, title, detail, compact }: { icon: IconName; title: string; detail: string; compact?: boolean }) {
  const { isAdmin } = useHousehold();
  const { openSettings } = useAppUi();
  return (
    <EmptyState
      icon={icon}
      title={title}
      detail={compact ? undefined : detail}
      action={
        isAdmin ? (
          <button className="btn btn-sm btn-primary" onClick={() => openSettings("location")}>
            Set location
          </button>
        ) : undefined
      }
    />
  );
}

/**
 * Card footer: source attribution, plus an honest note when the Worker is
 * serving a saved copy, plus the freshness badge.
 */
export function SkyFoot({ source, stamp, state, updatedAt, extra }: { source: ReactNode; stamp?: SkyStamp | null; state?: DataState; updatedAt?: number; extra?: ReactNode }) {
  return (
    <footer className="sky-foot">
      <span className="sky-source">{source}</span>
      {stamp?.servedFromCache && <span className="warn-text">Source unreachable · saved {relativeAge(stamp.fetchedAt)}</span>}
      {extra}
      {state && <StatusBadge state={state} updatedAt={updatedAt} />}
    </footer>
  );
}

/** Measure an element's content box (ResizeObserver fires only on resize, so this is cheap). */
export function useBoxSize<T extends HTMLElement>(): [React.RefObject<T | null>, { width: number; height: number }] {
  const ref = useRef<T>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => setBox((b) => (b.width === el.clientWidth && b.height === el.clientHeight ? b : { width: el.clientWidth, height: el.clientHeight }));
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, box];
}

/**
 * Drop optional parts until a card's content fits: returns a ref for the
 * card's (overflow-hidden, fixed-height) root and a level 0…max. Each render
 * that still overflows raises the level before paint; a new card size starts
 * again from 0. Widgets hide their least important parts first as it rises.
 */
export function useFitLevel<T extends HTMLElement>(max: number, size: { width: number; height: number }): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [state, setState] = useState({ key: "", level: 0 });
  const key = `${Math.round(size.width)}x${Math.round(size.height)}`;
  const level = state.key === key ? state.level : 0;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (level < max && el.scrollHeight > el.clientHeight + 1) setState({ key, level: level + 1 });
    else if (state.key !== key) setState({ key, level });
  });
  return [ref, level];
}

/** Like useBoxSize, for elements that mount later (after loading states): pass the returned callback as `ref`. */
export function useMeasure<T extends HTMLElement>(): [(el: T | null) => void, { width: number; height: number }] {
  const [el, setEl] = useState<T | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!el) return;
    const set = () => setBox((b) => (b.width === el.clientWidth && b.height === el.clientHeight ? b : { width: el.clientWidth, height: el.clientHeight }));
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, box];
}

export function useTimeFmt() {
  const { timeZone, settings } = useHousehold();
  const h24 = settings.clock24h;
  const time = (d: Date | null | undefined) => (d ? formatTime(d, timeZone, h24) : "—");
  /** "Today" / "Tomorrow" / "Yesterday" / "Sat" relative to `now`, in the household timezone. */
  const day = (d: Date, now: Date) => {
    const k = todayIn(timeZone, d);
    const t = todayIn(timeZone, now);
    if (k === t) return "Today";
    if (k === addDays(t, 1)) return "Tomorrow";
    if (k === addDays(t, -1)) return "Yesterday";
    return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(d);
  };
  const date = (d: Date) => new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }).format(d);
  /** Compact hour label: "3p" / "15". */
  const hour = (d: Date) => {
    if (h24) return new Intl.DateTimeFormat("en-US", { timeZone, hour: "2-digit", hourCycle: "h23" }).format(d);
    return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hour12: true }).format(d).replace(/\s?([AP])M/, (_, p: string) => p.toLowerCase());
  };
  return { time, day, date, hour, timeZone, h24 };
}

/** Current root font size in px (rem scales with the screen). */
export function remPx(): number {
  const n = typeof document === "undefined" ? 16 : parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(n) && n > 0 ? n : 16;
}

/** Distance in the household's preferred units (follows the temperature unit, like the weather card). */
export function useDistance() {
  const { settings } = useHousehold();
  const imperial = settings.temperatureUnit === "F";
  return (km: number, round = 1) => {
    const v = imperial ? km * 0.621371 : km;
    return `${(Math.round(v / round) * round).toLocaleString()} ${imperial ? "mi" : "km"}`;
  };
}
